import React from 'react';
import { Link } from 'gatsby';
import { GrCircleInformation } from 'react-icons/gr';
import { LuBookOpenText } from 'react-icons/lu';
import { GoPencil } from 'react-icons/go';
import { PiExamLight } from 'react-icons/pi';
import { generateNavLink, generateNavLabel } from '@src/utils/nav';
import { useClickMenu } from '@src/hooks/useClickMenu';

const SECTION_ICONS = {
  info: GrCircleInformation,
  book: LuBookOpenText,
  pencil: GoPencil,
  grade: PiExamLight,
};

const itemKey = (item) =>
  item.__typename === 'DatoCmsDropdownSection'
    ? item.title
    : item.linkType === 'page'
    ? item.page?.slug || item.label
    : item.category?.slug || item.label;

const NavNewMark = ({ isNew }) =>
  isNew ? (
    <span className="nav-new" aria-hidden="true">
      New
    </span>
  ) : null;

const DropdownItemContent = ({ item, hasChildren }) => (
  <>
    <span className="simple-dropdown-link-inner">{generateNavLabel(item)}</span>
    {hasChildren && (
      <span className="flyout-arrow" aria-hidden="true">
        ›
      </span>
    )}
  </>
);

const DropdownItem = ({ item, onDismiss }) => {
  const url = generateNavLink(item.linkType, item);
  const hasChildren = !item.disabled && item.subItems?.length > 0;
  const { ref, isOpen, toggle } = useClickMenu();
  if (!url && !hasChildren && !item.disabled) return null;

  return (
    <li
      ref={hasChildren ? ref : undefined}
      className={hasChildren ? `has-flyout${isOpen ? ' is-open' : ''}` : undefined}
    >
      {item.disabled ? (
        <span className="simple-dropdown-link is-disabled" aria-disabled="true">
          <DropdownItemContent item={item} hasChildren={false} />
        </span>
      ) : hasChildren ? (
        <button
          type="button"
          className="simple-dropdown-link"
          aria-expanded={isOpen}
          onClick={toggle}
        >
          <DropdownItemContent item={item} hasChildren />
        </button>
      ) : (
        <Link className="simple-dropdown-link" to={url} onClick={onDismiss}>
          <DropdownItemContent item={item} hasChildren={false} />
        </Link>
      )}
      {hasChildren && (
        <ul className="flyout-dropdown">
          {item.subItems.map((subItem) => {
            if (subItem.__typename === 'DatoCmsDropdownSection') {
              return (
                <li key={subItem.title} className="dropdown-section">
                  <span
                    className="dropdown-section-title"
                    style={subItem.color ? { color: subItem.color } : undefined}
                  >
                    {subItem.icon &&
                      SECTION_ICONS[subItem.icon] &&
                      React.createElement(SECTION_ICONS[subItem.icon], { width: 30, height: 30 })}
                    {subItem.title}
                  </span>
                  <ul className="dropdown-section-items">
                    {subItem.items.map((sectionItem) => (
                      <DropdownItem
                        key={itemKey(sectionItem)}
                        item={sectionItem}
                        onDismiss={onDismiss}
                      />
                    ))}
                  </ul>
                </li>
              );
            }
            return <DropdownItem key={itemKey(subItem)} item={subItem} onDismiss={onDismiss} />;
          })}
        </ul>
      )}
    </li>
  );
};

const ClickDropdown = ({ link, onCloseMobile, children }) => {
  const { ref, isOpen, toggle, dismiss } = useClickMenu(onCloseMobile);

  return (
    <li
      ref={ref}
      className={`has-simple-dropdown${isOpen ? ' is-open' : ''}${link.new ? ' is-new' : ''}`}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-label={link.new ? `${link.label} (New)` : undefined}
        onClick={toggle}
      >
        {link.label}
        <NavNewMark isNew={link.new} />
      </button>
      <ul className="simple-dropdown" key={isOpen ? 'open' : 'closed'}>
        {children(dismiss)}
      </ul>
    </li>
  );
};

const SimpleDropdown = ({ link, onCloseMobile }) => (
  <ClickDropdown link={link} onCloseMobile={onCloseMobile}>
    {(dismiss) =>
      link.subNavigation.map((item) => (
        <li key={item.slug}>
          <Link className="simple-dropdown-link" to={`/${item.slug}`} onClick={dismiss}>
            {item.title}
          </Link>
        </li>
      ))
    }
  </ClickDropdown>
);

const TwoLevelSimpleDropdown = ({ link, onCloseMobile }) => (
  <ClickDropdown link={link} onCloseMobile={onCloseMobile}>
    {(dismiss) =>
      link.items.map((item) => {
        if (item.__typename === 'DatoCmsDropdownSection') {
          return (
            <li key={item.title} className="dropdown-section">
              <span
                className="dropdown-section-title"
                style={item.color ? { color: item.color } : undefined}
              >
                {item.icon &&
                  SECTION_ICONS[item.icon] &&
                  React.createElement(SECTION_ICONS[item.icon], { width: 30, height: 30 })}
                {item.title}
              </span>
              <ul className="dropdown-section-items">
                {item.items.map((sectionItem) => (
                  <DropdownItem key={itemKey(sectionItem)} item={sectionItem} onDismiss={dismiss} />
                ))}
              </ul>
            </li>
          );
        }
        return <DropdownItem key={itemKey(item)} item={item} onDismiss={dismiss} />;
      })
    }
  </ClickDropdown>
);

const MainLink = ({ link, onCloseMobile }) => {
  if (link.items?.length > 0) {
    return <TwoLevelSimpleDropdown link={link} onCloseMobile={onCloseMobile} />;
  }

  if (link.subNavigation?.length > 0) {
    return <SimpleDropdown link={link} onCloseMobile={onCloseMobile} />;
  }

  return (
    <li key={link.slug} className={link.new ? 'is-new' : undefined}>
      <Link
        className="navigation-link w-nav-link"
        to={link.externalLink ? link.externalLink : `/${link.slug === 'home' ? '' : link.slug}`}
        onClick={onCloseMobile}
        aria-label={link.new ? `${link.label} (New)` : undefined}
      >
        {link.label}
        <NavNewMark isNew={link.new} />
      </Link>
    </li>
  );
};

export {
  DropdownItem,
  SimpleDropdown,
  TwoLevelSimpleDropdown,
  MainLink,
  NavNewMark,
  SECTION_ICONS,
};
